'use client';
import { ArrowUpRight, LoaderCircle } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

import { cn, declension } from '@/shared/utils';

const cardClassName =
	'group flex w-full cursor-pointer flex-col items-start overflow-hidden rounded-xl bg-gray-5/70 px-5 py-4 text-left ring-1 ring-inset ring-gray-4/40 transition-[background-color,box-shadow] duration-200 hover:bg-gray-5 hover:ring-gray-4 focus-visible:bg-gray-5 active:bg-gray-4/30 motion-reduce:transition-none md:p-5';

function ActionCardContent({
	title,
	image,
	isStart = false,
	showArrow = false,
}: {
	title: string;
	image: string;
	isStart?: boolean;
	showArrow?: boolean;
}) {
	return (
		<>
			<span className="flex min-h-8 w-full items-center justify-between gap-3">
				<span className="min-w-0 text-lg font-semibold leading-6">{title}</span>
				{showArrow && (
					<ArrowUpRight
						size={32}
						strokeWidth={1.5}
						aria-hidden="true"
						className="shrink-0 text-gray-3 transition-colors duration-200 group-hover:text-accent group-focus-visible:text-accent motion-reduce:transition-none"
					/>
				)}
			</span>
			<span className="relative mt-3 block h-[124px] w-full" aria-hidden="true">
				<Image
					src={image}
					alt=""
					width={isStart ? 256 : 144}
					height={isStart ? 256 : 144}
					sizes={isStart ? '256px' : '144px'}
					className={cn(
						'pointer-events-none absolute left-1/2 max-w-none -translate-x-1/2 select-none object-contain',
						isStart
							? '-bottom-32 size-64 transition-transform duration-300 ease-out group-hover:-translate-y-3 group-focus-visible:-translate-y-3 group-active:translate-y-1 group-active:scale-95 motion-reduce:transition-none'
							: '-top-2 size-36',
					)}
				/>
			</span>
		</>
	);
}

export function CandidateOverviewActions({
	minutes,
	active,
	pending,
	busy,
	canStart,
	onStart,
	onCareer,
}: {
	minutes: number;
	active: boolean;
	pending: boolean;
	busy: boolean;
	canStart: boolean;
	onStart: () => void;
	onCareer: () => void;
}) {
	const label = pending
		? 'Подбираем задачи…'
		: busy
			? 'Открываем интервью…'
			: active
				? 'Продолжить интервью'
				: 'Начать собеседование с AI';
	return (
		<div className="mt-4 grid gap-3 md:grid-cols-2 md:gap-4">
			<button
				type="button"
				onClick={onStart}
				aria-label={label}
				aria-busy={busy || pending}
				disabled={busy || pending || (!active && !canStart)}
				className={cn(
					cardClassName,
					'relative disabled:cursor-default disabled:opacity-60',
				)}
			>
				<ActionCardContent
					title={
						active
							? 'Продолжить интервью'
							: `Интервью займет ${minutes} ${declension(minutes, ['минуту', 'минуты', 'минут'])}`
					}
					image={
						active || busy || pending || !canStart
							? '/course-workspace/simulator-mascot.png'
							: '/course-workspace/simulator-keycap-start.png'
					}
					isStart={!active && !busy && !pending && canStart}
				/>
				{(busy || pending || (!active && !canStart)) && (
					<span
						role="status"
						className="absolute bottom-3 left-4 right-4 flex items-center justify-center gap-2 rounded-lg bg-white/95 p-2 text-sm"
					>
						{(busy || pending) && (
							<LoaderCircle
								size={16}
								aria-hidden="true"
								className="animate-spin motion-reduce:animate-none"
							/>
						)}
						{busy || pending ? label : 'Это собеседование уже пройдено.'}
					</span>
				)}
			</button>
			<Link
				href="/career-offer"
				aria-label="Подробнее: Оффер в Яндекс под ключ"
				onClick={onCareer}
				className={cardClassName}
			>
				<ActionCardContent
					showArrow
					title="Оффер в Яндекс под ключ"
					image="/course-workspace/algorithms-yandex-graph-standalone.png"
				/>
			</Link>
		</div>
	);
}
